/**
 * PulseDispositionsView — Stewardship · Pulse Dispositions (iter86.37).
 *
 * Tracked issues born from acknowledged Pulse considerations. Three
 * buckets: Unread (assign a Commons Admin), Open (investigating —
 * includes reopened), Closed (completed | dismissed, by whom).
 * Re-detection of a closed issue resurfaces on the briefing and, when
 * acknowledged, creates a NEW linked record with status `reopened` —
 * the old record stays here (append-only).
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const KIND_PILL = {
    aggregate_risk: "bg-violet-900/40 text-violet-200 border-violet-700",
    drift_concern: "bg-amber-900/40 text-amber-200 border-amber-700",
};
const STATUS_PILL = {
    unread: "bg-sky-900/40 text-sky-200 border-sky-700",
    open: "bg-amber-900/40 text-amber-200 border-amber-700",
    reopened: "bg-rose-900/40 text-rose-200 border-rose-700",
    closed: "bg-zinc-800 text-zinc-400 border-zinc-600",
};

function IssueRow({ it, onAssign, onStatus }) {
    return (
        <div className="border-t border-zinc-800 pt-2 text-[11px] space-y-1" data-testid={`pulse-issue-row-${it.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
                <span className={`pill text-[9px] font-bold uppercase border ${KIND_PILL[it.kind] || ""}`}>
                    {it.kind === "aggregate_risk" ? "Aggregate risk" : "Drift concern"}
                </span>
                <span className={`pill text-[9px] font-bold uppercase border ${STATUS_PILL[it.status] || ""}`} data-testid={`pulse-issue-status-${it.id}`}>
                    {it.status}
                </span>
                <span className="font-bold text-white">{it.title}</span>
                <span className="text-zinc-600">
                    iter{it.first_seen_iter}{it.last_seen_iter !== it.first_seen_iter ? ` → iter${it.last_seen_iter}` : ""} · seen ×{it.seen_count}
                </span>
            </div>
            <p className="text-zinc-400">{it.detail}</p>
            <p className="text-sky-200">→ {it.action_hint}</p>
            <div className="flex items-center gap-2 flex-wrap">
                {it.assigned_to && <span className="text-[10px] text-zinc-400">Assigned: <span className="text-white font-bold">{it.assigned_to}</span></span>}
                {it.status === "closed" && (
                    <span className="text-[10px] text-zinc-500" data-testid={`pulse-issue-closed-meta-${it.id}`}>
                        {it.closed_reason} by {it.closed_by} · {(it.closed_at || "").slice(0, 10)}{it.closed_note ? ` — ${it.closed_note}` : ""}
                    </span>
                )}
                {it.predecessor_id && <span className="text-[9px] text-rose-300">reopened from a closed record</span>}
                <div className="flex-1" />
                {it.status !== "closed" && (
                    <>
                        <button onClick={() => onAssign(it)} className="brutal-btn-ghost text-[10px] px-2 py-1" data-testid={`pulse-issue-assign-${it.id}`}>Assign</button>
                        {it.status === "unread" && (
                            <button onClick={() => onStatus(it, "open")} className="brutal-btn text-[10px] px-2 py-1" data-testid={`pulse-issue-open-${it.id}`}>Start investigating</button>
                        )}
                        <button onClick={() => onStatus(it, "closed", "completed")} className="brutal-btn text-[10px] px-2 py-1 bg-emerald-800 text-white" data-testid={`pulse-issue-complete-${it.id}`}>Complete</button>
                        <button onClick={() => onStatus(it, "closed", "dismissed")} className="brutal-btn-ghost text-[10px] px-2 py-1 text-zinc-400" data-testid={`pulse-issue-dismiss-${it.id}`}>Dismiss</button>
                    </>
                )}
                {it.status === "closed" && (
                    <button onClick={() => onStatus(it, "reopened")} className="brutal-btn-ghost text-[10px] px-2 py-1" data-testid={`pulse-issue-reopen-${it.id}`}>Reopen</button>
                )}
            </div>
        </div>
    );
}

function Bucket({ title, subtitle, rows, testid, onAssign, onStatus }) {
    return (
        <div className="border border-zinc-700 rounded p-3 space-y-2" data-testid={testid}>
            <p className="font-bold text-white text-xs uppercase">{title} ({rows.length})</p>
            <p className="text-[10px] text-zinc-500">{subtitle}</p>
            {rows.length === 0 && <p className="text-[11px] text-zinc-600 italic">Empty.</p>}
            {rows.map((it) => <IssueRow key={it.id} it={it} onAssign={onAssign} onStatus={onStatus} />)}
        </div>
    );
}

export function PulseDispositionsView() {
    const [rows, setRows] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.pulseIssues("all");
            setRows(r.issues || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load Pulse issues");
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const assign = async (it) => {
        const email = window.prompt("Assign to (Commons Admin email):", it.assigned_to || "");
        if (!email) return;
        try {
            await api.pulseIssueAssign(it.id, email);
            toast.success(`Assigned to ${email}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Assign failed");
        }
    };

    const setStatus = async (it, to, reason = null) => {
        let note = null;
        if (to === "closed") {
            note = window.prompt(reason === "completed" ? "Completion note (what was done):" : "Dismissal reason:");
            if (note === null) return;
        }
        try {
            await api.pulseIssueStatus(it.id, { to, reason, note });
            toast.success(to === "closed" ? `Closed (${reason})` : `Status: ${to}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Status change failed");
        }
    };

    if (rows === null) return <p className="text-xs text-zinc-500">Loading…</p>;

    const unread = rows.filter((r) => r.status === "unread");
    const open = rows.filter((r) => r.status === "open" || r.status === "reopened");
    const closed = rows.filter((r) => r.status === "closed");

    return (
        <div className="space-y-4" data-testid="pulse-dispositions-view">
            <p className="text-[11px] text-zinc-400">
                Issues acknowledged from Pulse briefing considerations. Each briefing re-checks:
                a still-detected issue keeps its record here (seen count grows); a closed issue
                that resurfaces gets flagged on the briefing and reopens as a new linked record.
            </p>
            <Bucket title="Unread" testid="pulse-issues-unread"
                subtitle="Acknowledged, not yet picked up — assign a Commons Admin."
                rows={unread} onAssign={assign} onStatus={setStatus} />
            <Bucket title="Open — investigating" testid="pulse-issues-open"
                subtitle="Being worked. Includes reopened records."
                rows={open} onAssign={assign} onStatus={setStatus} />
            <Bucket title="Closed" testid="pulse-issues-closed"
                subtitle="Completed or dismissed, with who and why. Append-only history."
                rows={closed} onAssign={assign} onStatus={setStatus} />
        </div>
    );
}
